"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Icon from "@/components/icons/Icons";
import StepperSidebar from "../process-parts/StepperSidebar";
import StepNavigation from "../process-parts/StepNavigation";
import PreRetirementFlow from "../process-flows/PreRetirementFlow";
import PostRetirementFlow from "../process-flows/PostRetirementFlow";
import {
  DEFAULT_RETIREMENT_DURATION,
  getClosestPortfolioAmount,
  MAX_CUSTOM_RETIREMENT_CORPUS,
  MAX_RETIREMENT_DURATION,
  MIN_CUSTOM_RETIREMENT_CORPUS,
  MIN_RETIREMENT_DURATION,
  MIN_CURRENT_AGE,
  MAX_CURRENT_AGE,
  DEFAULT_MIN_RETIREMENT_AGE,
  MAX_RETIREMENT_AGE,
} from "@/utils/financialPageFunctions";
import { apiPost } from "@/utils/apiClient";
import { useLoginModal } from "@/utils/useLoginModal";
import { AnimatePresence, motion } from "framer-motion";
import { DASHBOARD_ROOT } from "@/utils/gloableVariables";
import { addToast } from "@/components/ui/CustomToastManager";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";
import { getPortfolioInvestmentLinks } from "@/lib/prismaController";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import { Goal, RetirementSummaryValues } from "@/type/financialPageTypes";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { useDispatch } from "react-redux";

// Fallback ids used only if the API question list is ever empty, so
// validation still has something stable to key off of.
const FALLBACK_AMOUNT_QUESTION_ID = 9201;
const FALLBACK_DURATION_QUESTION_ID = 9203;

export default function RetirementPlanningModal({
  currentGoal,
  questions,
}: {
  currentGoal: Goal;
  questions: PortfolioApiQuestion[];
}) {
  const [validateTrigger, setValidateTrigger] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  const [answers, setAnswers] = useState<{
    [key: number]: {
      question_id: number;
      option_id: number;
      answer: string;
    };
  }>({});

  // Populated by the "Your Summary" step of whichever flow is active, so
  // checkout/submit uses the same corpus/SIP/rate/tenure the user sees there.
  const [summaryValues, setSummaryValues] = useState<RetirementSummaryValues | null>(null);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const basePath = "/financial-planning/retirement-planning";
  const isPostRetirement = currentGoal?.url === "post-retirement";

  useEffect(() => {
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, []);

  // -------- Group API questions into stepper steps (Pre-Retirement only) ---------
  const apiGroupedSteps = useMemo(() => {
    const map = new Map<string, { title: string; questions: PortfolioApiQuestion[] }>();

    questions.forEach((question) => {
      const title = question.stepper_title?.trim() || "Untitled";
      const existing = map.get(title);
      if (existing) {
        existing.questions.push(question);
      } else {
        map.set(title, { title, questions: [question] });
      }
    });

    return Array.from(map.values()).map((item, index) => ({
      id: index + 1,
      title: item.title,
      questions: item.questions,
    }));
  }, [questions]);

  // Both flows use the API's stepper_title for their question-driven steps; ------ Post-Retirement then appends its two fixed, non-API steps (scenario + summary), Pre-Retirement appends just the summary.
  const groupedSteps = useMemo(() => {
    if (isPostRetirement) {
      return [
        ...apiGroupedSteps,
        {
          id: apiGroupedSteps.length + 1,
          title: "Retirement Scenario",
          questions: [] as PortfolioApiQuestion[],
        },
        {
          id: apiGroupedSteps.length + 2,
          title: "Your Summary",
          questions: [] as PortfolioApiQuestion[],
        },
      ];
    }

    return [
      ...apiGroupedSteps,
      {
        id: apiGroupedSteps.length + 1,
        title: "Your Summary",
        questions: [] as PortfolioApiQuestion[],
      },
    ];
  }, [isPostRetirement, apiGroupedSteps]);

  // -------- Post-Retirement question ids (step 1 = amount, step 2 = duration) ---------
  const postRetirementQuestions = useMemo(
    () => [...questions].sort((a, b) => (a.step ?? 0) - (b.step ?? 0)),
    [questions],
  );

  const postRetirementAmountQuestionId = postRetirementQuestions[0]?.id ?? FALLBACK_AMOUNT_QUESTION_ID;
  const postRetirementDurationQuestionId = postRetirementQuestions[1]?.id ?? FALLBACK_DURATION_QUESTION_ID;

  // -------- Current Step State ---------
  const [currentStep, setCurrentStep] = useState<number>(1);
  const isLastStep = currentStep === groupedSteps.length;

  // Reset step/answers when switching between the Pre- and Post-Retirement
  // flows (client-side navigation between the two slugs), so stale state from
  // one flow never leaks into the other.
  useEffect(() => {
    setCurrentStep(1);
    setAnswers({});
    setSummaryValues(null);
    setValidateTrigger(0);
  }, [isPostRetirement]);

  // -------- Validation ---------
  // Returns true if valid. On failure, bumps validateTrigger so screens reveal field errors.
  const validatePreRetirementStep = (): boolean => {
    // Step 1: must choose a pre-built plan OR a planning mode
    if (currentStep === 1) {
      if (!answers[110] && !answers[111]) {
        setValidateTrigger((n) => n + 1);
        return false;
      }
    }

    // Step 2: dynamic current age and retirement age validations
    if (currentStep === 2) {
      const currentAge = answers[112] ? parseInt(answers[112].answer) : 28;
      const retirementAge = answers[145] ? parseInt(answers[145].answer) : 65;

      const minRetirementAge = currentAge > 40 ? currentAge + 5 : DEFAULT_MIN_RETIREMENT_AGE;

      if (
        isNaN(currentAge) ||
        currentAge < MIN_CURRENT_AGE ||
        currentAge > MAX_CURRENT_AGE ||
        isNaN(retirementAge) ||
        retirementAge < minRetirementAge ||
        retirementAge > MAX_RETIREMENT_AGE
      ) {
        setValidateTrigger((n) => n + 1);
        return false;
      }
    }

    // Step 3: custom mode → only corpus ≥₹10L; all other modes → income ₹15k–₹10Cr + % 5–50 (plan/salary)
    if (currentStep === 3) {
      const isCustomMode = answers[111]?.answer === "Want to enter Custom Retirement amount";

      if (isCustomMode) {
        const customAmt = parseFloat((answers[149]?.answer ?? "").replace(/,/g, ""));
        if (!answers[149]?.answer || isNaN(customAmt) || customAmt < 10_00_000) {
          setValidateTrigger((n) => n + 1);
          return false;
        }
      } else {
        const isExpensesMode = answers[111]?.answer === "Based on desired future expenses";
        const incomeAnswer = isExpensesMode ? answers[148]?.answer : answers[146]?.answer;
        const income = parseFloat((incomeAnswer ?? "").replace(/,/g, ""));
        const minIncome = isExpensesMode ? 10_000 : 15_000;

        if (!incomeAnswer || isNaN(income) || income < minIncome || income > 10_00_00_000) {
          setValidateTrigger((n) => n + 1);
          return false;
        }

        const hasPlan = !!answers[110];
        const isSalaryMode = answers[111]?.answer === "Based on my salary";

        if (hasPlan || isSalaryMode) {
          const percent = answers[147]?.answer !== undefined ? Number(answers[147].answer) : 25;
          if (isNaN(percent) || percent < 5 || percent > 50) {
            setValidateTrigger((n) => n + 1);
            return false;
          }
        }
      }
    }

    return true;
  };

  // Step 1: must choose a pre-built plan OR a valid custom corpus (both resolve
  // to a single answer keyed by the real API question id)
  // Step 2: retirement duration within range
  // Steps 3–4 (scenario/summary) are informational, no user input to validate
  const validatePostRetirementStep = (): boolean => {
    if (currentStep === 1) {
      const amount = parseFloat((answers[postRetirementAmountQuestionId]?.answer ?? "").replace(/,/g, ""));
      if (
        !answers[postRetirementAmountQuestionId]?.answer ||
        isNaN(amount) ||
        amount < MIN_CUSTOM_RETIREMENT_CORPUS ||
        amount > MAX_CUSTOM_RETIREMENT_CORPUS
      ) {
        setValidateTrigger((n) => n + 1);
        return false;
      }
    }

    if (currentStep === 2) {
      const duration = answers[postRetirementDurationQuestionId]
        ? parseInt(answers[postRetirementDurationQuestionId].answer)
        : DEFAULT_RETIREMENT_DURATION;
      if (isNaN(duration) || duration < MIN_RETIREMENT_DURATION || duration > MAX_RETIREMENT_DURATION) {
        setValidateTrigger((n) => n + 1);
        return false;
      }
    }

    return true;
  };

  const validateCurrentStep = (): boolean =>
    isPostRetirement ? validatePostRetirementStep() : validatePreRetirementStep();

  // -------- Getting Portfolios ---------
  const getPortfolios = async ({ answerKey }: { answerKey: string }) => {
    if (!summaryValues) {
      addToast({ title: "", description: "Please review your summary before submitting.", status: "failed" });
      return;
    }

    const { corpus, sipValue: rawSipValue, expectedReturnRate, tenure } = summaryValues;
    // Ceil to the nearest ₹100 so downstream SIP/lumpsum amounts are cleanly divisible by 100.
    const sipValue = Math.ceil(rawSipValue / 100) * 100;

    const payload = {
      slug: currentGoal.url,
      sipValue: sipValue,
      tenureInYears: tenure,
    };

    try {
      const portfolioRes = await getPortfolioInvestmentLinks(payload);

      if (!portfolioRes) {
        addToast({ title: "", description: "Failed to fetch the portfolio.", status: "failed" });
        throw new Error("Failed to fetch the portfolio.");
      }

      addToast({ title: "", description: "Portfolios Fetched Successfully", status: "success" });

      const { portfolioData, investmentType } = portfolioRes;

      const buyFunds = portfolioData.schemes.map((s) => ({
        selected_scheme_id: s.id,
        scheme_code: s.scheme_code,
        percentage: s.percentage,
      }));

      const newSIPValue = getClosestPortfolioAmount(
        sipValue,
        portfolioData.schemes.map((s) => s.percentage),
      );

      const portfolioPayload = {
        sip_amt: investmentType === "Sip" ? newSIPValue : 0,
        lumpsum_amt: investmentType === "Lumpsum" ? newSIPValue : 0,
        duration: tenure,
        sip_date: 1,
        calculated_amt: corpus,
        expected_return_rate: expectedReturnRate,
        portfolio_id: portfolioData.id,
        portfolio_title: currentGoal.title,
        inv_type: investmentType.toLowerCase(),
        goal_id: currentGoal.goal_id,
        answer_key: answerKey,
      };

      const savedGoalPayload = {
        clientPortfolioSchemes: buyFunds,
        portfolioData: portfolioPayload,
      };

      const res = await apiPost("saveGoalPortfolio_v1", savedGoalPayload);
      if (res?.status == 1) {
        window.location.href = `${DASHBOARD_ROOT}buy-portfolio/${res.savedGoalId}`;
      } else {
        throw new Error(res?.message || "Save failed");
      }
    } catch (error) {
      addToast({ title: "", description: String(error) ?? "Something went wrong", status: "failed" });

      console.error("Something went wrong", error);
    }
  };

  // -------- Handle Submit ---------
  const handleSubmit = async () => {
    if (!isLoggedIn) {
      open();
      setModalKey(currentGoal.url);
      return;
    }

    setIsSubmitting(true);

    try {
      try {
        const res = await apiPost("feedback/submitFeedback", {
          subject: currentGoal?.title || "",
          answerList: answers,
        });

        if (res?.status !== 1) throw new Error(res?.message || "Submit failed");

        addToast({ title: "", description: "Answer Submitted Successfully", status: "success" });
        getPortfolios({ answerKey: res.answer_key });
      } catch (error) {
        console.error("Something went wrong", error);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPreBuiltPlan = !isPostRetirement && !!answers[110];

  // -------- Next Navigation ---------
  const nextStep = async () => {
    const isValid = validateCurrentStep();
    if (!isValid) return;

    if (!isLastStep) {
      // Skip income step (3) when a pre-built plan is selected
      const increment = isPreBuiltPlan && currentStep === 2 ? 2 : 1;
      setCurrentStep((prev) => prev + increment);
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    } else {
      await handleSubmit();
    }
  };

  // -------- Back Navigation ---------
  const prevStep = () => {
    setCurrentStep((prev) => {
      if (prev <= 1) return prev;
      // Skip income step (3) when a pre-built plan is selected
      const decrement = isPreBuiltPlan && prev === 4 ? 2 : 1;
      return Math.max(1, prev - decrement);
    });
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  };

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key="financial-modal"
          className="fixed inset-0 z-5001 flex items-center justify-center backdrop-blur-xl px-4 bg-primaryBlue/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
        >
          <div className="relative w-full max-w-section flex flex-col">
            <div className="flex items-center justify-end gap-2 mb-2">
              <button
                onClick={() => dispatch(openHelpModal())}
                className="hidden lg:inline-block relative rounded-full font-semibold py-2 px-4 text-sm border border-white transition duration-400 bg-transparent text-white hover:text-primaryBlue hover:bg-[#f5f9ff]"
              >
                Need Help?
              </button>

              <Link
                href={basePath}
                aria-label="Cross The Modal Button"
                className="group hidden lg:flex items-center justify-center rounded-full cursor-pointer w-10 h-10 transition duration-400 bg-[#7696ca] hover:bg-white"
              >
                <Icon.crossIcon className="text-white w-4.5 h-4.5 transition duration-400 group-hover:text-primaryBlue" />
              </Link>
            </div>

            <motion.div
              className="relative flex flex-col lg:flex-row justify-between gap-3.5 sm:gap-6 bg-transparent overflow-y-auto scrollbar-none"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
            >
              <StepperSidebar
                groupedSteps={groupedSteps}
                mainTitle={currentGoal.title}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                scrollContainerRef={scrollContainerRef}
                skippedSteps={isPreBuiltPlan ? [3] : []}
              />

              <div className="w-full max-w-194.25 mx-auto lg:mx-0">
                <div className="rounded-2xl sm:rounded-4xl h-121.25 max-h-[70vh] sm:h-132.5 overflow-y-auto scrollbar-none bg-white shadow-[-0.9px_5.41px_13.52px_0px_#4444441A]">
                  {isPostRetirement ? (
                    <PostRetirementFlow
                      currentStep={currentStep}
                      questions={questions}
                      onAnswersChangeAction={setAnswers}
                      onSummaryChangeAction={setSummaryValues}
                      scrollContainerRef={scrollContainerRef}
                      validateTrigger={validateTrigger}
                    />
                  ) : (
                    <PreRetirementFlow
                      currentStep={currentStep}
                      questions={questions}
                      onAnswersChangeAction={setAnswers}
                      onSummaryChangeAction={setSummaryValues}
                      scrollContainerRef={scrollContainerRef}
                      validateTrigger={validateTrigger}
                    />
                  )}
                </div>

                <StepNavigation
                  currentStep={currentStep}
                  onContinue={nextStep}
                  onBack={prevStep}
                  isLoading={isSubmitting}
                  disabled={
                    isLastStep &&
                    !isPostRetirement &&
                    (!summaryValues?.sipValue || summaryValues.sipValue <= 0 || summaryValues.sipValue % 100 !== 0)
                  }
                  continueText={isLastStep ? "Submit Plan" : "Continue"}
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
